"use client";

import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Share2, 
  Copy, 
  Check, 
  Facebook, 
  Twitter, 
  Linkedin, 
  Mail,
  MessageCircle,
  ExternalLink
} from "lucide-react";
import { toast } from "sonner";

interface ShareModalProps {
  title: string;
  url?: string;
  description?: string;
  children: React.ReactNode;
  className?: string;
}

export default function ShareModal({ title, url, description, children, className }: ShareModalProps) {
  const [copied, setCopied] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [currentUrl, setCurrentUrl] = useState('');

  useEffect(() => {
    setCurrentUrl(window.location.href);
  }, []);

  const shareUrl = url || currentUrl;
  
  const shareData = {
    title,
    text: description || title,
    url: shareUrl,
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error("Failed to copy link");
    }
  };

  const shareViaNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        console.log("Error sharing:", err);
      }
    }
  };

  const shareViaSocial = (platform: string) => {
    let socialShareUrl = "";
    
    switch (platform) {
      case "facebook":
        socialShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
        break;
      case "twitter":
        socialShareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(shareUrl)}`;
        break;
      case "linkedin":
        socialShareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
        break;
      case "whatsapp":
        socialShareUrl = `https://wa.me/?text=${encodeURIComponent(`${title} ${shareUrl}`)}`;
        break;
      case "email":
        socialShareUrl = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`${description || title}\n\n${shareUrl}`)}`;
        break;
      default:
        return;
    }
    
    window.open(socialShareUrl, "_blank", "width=600,height=400");
  };

  const shareOptions = [
    {
      name: "Facebook",
      icon: Facebook,
      color: "bg-blue-600 hover:bg-blue-700",
      action: () => shareViaSocial("facebook"),
    },
    {
      name: "Twitter",
      icon: Twitter,
      color: "bg-sky-500 hover:bg-sky-600",
      action: () => shareViaSocial("twitter"),
    },
    {
      name: "LinkedIn",
      icon: Linkedin,
      color: "bg-blue-700 hover:bg-blue-800",
      action: () => shareViaSocial("linkedin"),
    },
    {
      name: "WhatsApp",
      icon: MessageCircle,
      color: "bg-green-600 hover:bg-green-700",
      action: () => shareViaSocial("whatsapp"),
    },
    {
      name: "Email",
      icon: Mail,
      color: "bg-gray-600 hover:bg-gray-700",
      action: () => shareViaSocial("email"),
    },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5" />
            Share this story
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Social Media Buttons */}
          <div>
            <Label className="text-sm font-medium mb-3 block">Share via social media</Label>
            <div className="grid grid-cols-3 gap-3">
              {shareOptions.map((option) => (
                <Button
                  key={option.name}
                  variant="outline"
                  className={`${option.color} text-white border-0 hover:scale-105 transition-transform`}
                  onClick={option.action}
                >
                  <option.icon className="h-4 w-4" />
                  <span className="ml-2 text-xs">{option.name}</span>
                </Button>
              ))}
            </div>
          </div>

          {/* Native Share (if available) */}
          {typeof navigator !== 'undefined' && typeof navigator.share === 'function' && (
            <div>
              <Button
                onClick={shareViaNative}
                className="w-full bg-green-600 hover:bg-green-700 text-white"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Share via device
              </Button>
            </div>
          )}

          {/* Copy Link */}
          <div>
            <Label htmlFor="share-url" className="text-sm font-medium mb-2 block">
              Or copy the link
            </Label>
            <div className="flex gap-2">
              <Input
                id="share-url"
                value={shareUrl}
                readOnly
                className="flex-1"
              />
              <Button
                variant="outline"
                size="icon"
                onClick={copyToClipboard}
                className="shrink-0"
              >
                {copied ? (
                  <Check className="h-4 w-4 text-green-600" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
